import React from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Widget from './Widget'
import { useWidgetEditor } from './state'

interface SortableItemProps {
  id: string
  children: React.ReactNode
}

export function SortableItem({ id, children }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  )
}

interface SortableContainerProps {
  items: string[]
  dir: 'horizontal' | 'vertical'
  onReorder: (newOrder: string[]) => void
  children: React.ReactNode
}

export function SortableContainer({
  items,
  onReorder,
  dir,
  children,
}: SortableContainerProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id as string)
      const newIndex = items.indexOf(over.id as string)
      const newOrder = arrayMove(items, oldIndex, newIndex)
      onReorder(newOrder)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items}
        strategy={
          dir === 'horizontal'
            ? horizontalListSortingStrategy
            : verticalListSortingStrategy
        }
      >
        {children}
      </SortableContext>
    </DndContext>
  )
}

export function SortableWidgetChildren({
  id,
  dir,
}: {
  id: string
  dir: 'horizontal' | 'vertical'
}) {
  const widgetActions = useWidgetEditor()
  const children = widgetActions.useChildren(id)
  const isReordering = widgetActions.useIsEditMode(id, 'reorder')

  if (isReordering) {
    return (
      <SortableContainer
        items={children}
        onReorder={widgetActions.setOrder}
        dir={dir}
      >
        {children.map((id: string) => (
          <SortableItem key={id} id={id}>
            <Widget id={id} />
          </SortableItem>
        ))}
      </SortableContainer>
    )
  } else {
    return (
      <>
        {children.map((id: string) => (
          <Widget key={id} id={id} />
        ))}
      </>
    )
  }
}
